import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { env } from '@noor/config';
import { createMerchantQueryClient } from '@/app/providers/query-client';
import { AppProviders, i18n } from '@/app/providers';
import { createAppRouter } from '@/app/router/router';
import '@fontsource-variable/geist-mono/index.css';
import '@/app/styles/index.css';

/**
 * Start MSW before the app boots so the very first query is intercepted. MSW
 * is a GAP-FILLER (see .env): it answers only for screens with no real
 * endpoint yet; everything else bypasses to the live API.
 *
 * The e2e suite opts out via a localStorage flag: MSW's service worker sits
 * below Playwright's `page.route`, so it would answer requests the test
 * intends to stub.
 *
 * MUST NEVER THROW. Service-worker registration hard-fails in environments a
 * normal page survives — an untrusted TLS certificate (a page cert warning is
 * click-throughable, a SW script fetch is not), or an insecure context (plain
 * http on a non-localhost host). `bootstrap()` awaits this before rendering,
 * so an uncaught failure here would blank the ENTIRE app to mock a corner of
 * it. On failure: warn, skip mocking, boot — the gap screens degrade to their
 * query-error states while every real-API screen works normally.
 */
async function enableMocking(): Promise<void> {
  if (!env.VITE_ENABLE_MSW) return;
  if (localStorage.getItem('e2e-disable-msw') === 'true') return;
  try {
    const { worker } = await import('@/app/mock');
    await worker.start({ onUnhandledRequest: 'bypass' });
  } catch (error) {
    console.warn(
      '[msw] mock worker failed to start — continuing without mock data ' +
        '(likely an untrusted TLS certificate or insecure context):',
      error,
    );
  }
}

async function bootstrap(): Promise<void> {
  await enableMocking();

  const queryClient = createMerchantQueryClient();

  // Server-resolved text is cached under keys that say nothing about the
  // locale, so a language switch would keep serving the previous language's
  // strings until each query happened to go stale. Refetching everything is
  // correct here rather than heavy-handed: `x-locale` changes the CONTENT of
  // essentially every response (see `@noor/api`'s `locale.ts`), and a switch is
  // a rare, deliberate, already-disruptive action.
  //
  // Wired here rather than in the three language switchers because this is
  // where the QueryClient lives — one place to be right instead of three to
  // keep in sync.
  i18n.on('languageChanged', () => {
    void queryClient.invalidateQueries();
  });

  const router = createAppRouter(queryClient);

  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element #root not found');

  createRoot(rootElement).render(
    <StrictMode>
      <AppProviders queryClient={queryClient}>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
}

void bootstrap();
